import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Link, Snackbar } from "@mui/material";
import { APP_ENVIRONMENT, APP_VERSION } from "../config";
import { useAuth } from "../context/AuthContext";
import { hasPropertyWriteAccess } from "../utils/roleAccess";
import { submitFeedbackCollector } from "../api/feedbackCollectorApi";
import { getTelemetryContext } from "../telemetry/appInsights";
import FeedbackCollectorDialog from "./FeedbackCollectorDialog";
import { FEEDBACK_DIALOG_Z_INDEX } from "./overlayZIndex";
import {
  capturePageScreenshot,
  FEEDBACK_EXCLUDE_ATTR,
} from "./capturePageScreenshot";
import { getSessionId, getSnapshot, installConsoleCapture, newId, recordRoute } from "./sessionBuffer";

const CATEGORIES = [
  { value: "Bug", label: "Bug" },
  { value: "Change request", label: "Change request" },
  { value: "Question", label: "Question" },
];

const FeedbackActionsContext = createContext({
  openCollector: () => {},
  canSend: false,
});

export function useFeedbackActions() {
  return useContext(FeedbackActionsContext);
}

function errorMessage(err) {
  const data = err?.response?.data;
  if (typeof data?.error === "string" && data.error) return data.error;
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  return "Unable to send feedback. Please try again.";
}

function buildReport({ user, comment, category }) {
  const roles = user?.claims_role || user?.roles || [];
  return {
    schemaVersion: 1,
    submissionId: newId(),
    sessionId: getSessionId(),
    timestamp: new Date().toISOString(),
    environment: APP_ENVIRONMENT || "",
    appVersion: APP_VERSION || "unknown",
    page: {
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      title: document.title,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio || 1,
      },
    },
    user: {
      name: user?.name || "",
      email: user?.email || user?.username || "",
      b2cObjectId: user?.b2c_object_id || "",
      roles: Array.isArray(roles) ? roles : [],
    },
    comment,
    category,
    history: getSnapshot().history,
    telemetry: getTelemetryContext(),
    client: {
      userAgent: navigator.userAgent,
      language: navigator.language || "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    },
  };
}

export default function FeedbackCollector({ children }) {
  const location = useLocation();
  const { user, permissions } = useAuth();
  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [captureFailed, setCaptureFailed] = useState(false);
  const [capturedAt, setCapturedAt] = useState("");
  const [toast, setToast] = useState({ open: false, severity: "success", message: "", url: "" });
  const busyRef = useRef(false);

  useEffect(() => {
    installConsoleCapture();
    recordRoute(location.pathname, location.search);
  }, [location.pathname, location.search]);

  const roles = user?.claims_role || user?.roles || [];
  const canSend = hasPropertyWriteAccess(roles, permissions);

  const openCollector = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setCaptureFailed(false);
    setCapturedAt(new Date().toISOString());
    try {
      const blob = await capturePageScreenshot(FEEDBACK_EXCLUDE_ATTR);
      setScreenshot(blob);
    } catch (err) {
      console.warn("Feedback screenshot failed:", err);
      setScreenshot(null);
      setCaptureFailed(true);
    }
    setOpen(true);
    busyRef.current = false;
  };

  const closeCollector = () => {
    setOpen(false);
    setScreenshot(null);
    setCaptureFailed(false);
  };

  const onSubmit = async ({ category, comment, image }) => {
    const report = buildReport({ user, comment, category });
    const result = await submitFeedbackCollector(report, image);
    setToast({
      open: true,
      severity: "success",
      message: result?.id ? `Feedback sent · #${result.id}` : "Feedback sent",
      url: result?.url || "",
    });
  };

  const userLine =
    (user?.name || "Signed-in user") + (user?.email ? ` · ${user.email}` : "");

  return (
    <FeedbackActionsContext.Provider value={{ openCollector, canSend }}>
      {children}

      <FeedbackCollectorDialog
        open={open}
        onClose={closeCollector}
        excludeAttr={FEEDBACK_EXCLUDE_ATTR}
        categoryOptions={CATEGORIES}
        userLine={userLine}
        capturedAt={capturedAt}
        screenshot={screenshot}
        captureFailed={captureFailed}
        onSubmit={async (payload) => {
          try {
            await onSubmit(payload);
          } catch (err) {
            setToast({
              open: true,
              severity: "error",
              message: errorMessage(err),
              url: "",
            });
            throw err;
          }
        }}
      />

      <Snackbar
        open={toast.open}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: FEEDBACK_DIALOG_Z_INDEX + 1 }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((current) => ({ ...current, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
          {toast.url && (
            <>
              {" "}
              <Link
                href={toast.url}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="always"
              >
                View in Azure DevOps
              </Link>
            </>
          )}
        </Alert>
      </Snackbar>
    </FeedbackActionsContext.Provider>
  );
}
